// Raw-value enums of the synced Swift models (WEB_PLAN.md §0, §2.3). Payloads carry the raw strings; a value that is not
// in these lists makes the item undecodable on iOS, so the web only ever writes values from here. Every list is in Swift
// declaration order (= `allCases`), which the Swift cross-check test (test/unit/contracts.swift.test.ts) asserts, and
// titles are the strings the iOS `title` properties return, so both apps label things the same way.
//
// Sources: GroundTeam/Models/{Record,Activity,User,Territory,Route,Pipeline,CompanySettings,Automation,Engagement}.swift.

/** Type guard for a raw value of one of the enums below. */
export function isEnumValue<T extends string>(list: readonly T[], value: unknown): value is T {
  return typeof value === 'string' && (list as readonly string[]).includes(value);
}

// MARK: Record.swift

/** `RecordType`: a pin is a Lead, an Account (won customer / business) or a Contact (person). */
export const RECORD_TYPES = ['lead', 'account', 'contact'] as const;
export type RecordType = (typeof RECORD_TYPES)[number];
/** `RecordType.title` (`rawValue.capitalized`). */
export const RECORD_TYPE_TITLES: Readonly<Record<RecordType, string>> = {
  lead: 'Lead',
  account: 'Account',
  contact: 'Contact',
};
/** `RecordType.plural` (`title + "s"`). */
export const RECORD_TYPE_PLURALS: Readonly<Record<RecordType, string>> = {
  lead: 'Leads',
  account: 'Accounts',
  contact: 'Contacts',
};

/** `Priority` raw values (Swift cases `a`…`f`; A = hottest). */
export const PRIORITIES = ['A', 'B', 'C', 'D', 'F'] as const;
export type Priority = (typeof PRIORITIES)[number];
export const PRIORITY_TITLES: Readonly<Record<Priority, string>> = {
  A: 'A · Hot',
  B: 'B · Warm',
  C: 'C · Neutral',
  D: 'D · Cool',
  F: 'F · Cold',
};
/** `Priority.colorHex` (map pins and chips use the same colors as iOS). */
export const PRIORITY_COLORS: Readonly<Record<Priority, string>> = {
  A: '#DB4038',
  B: '#F96A3C',
  C: '#F5B400',
  D: '#2673E6',
  F: '#8C919E',
};
/** `Priority.score` (sorting and next-best-action weighting). */
export const PRIORITY_SCORES: Readonly<Record<Priority, number>> = { A: 5, B: 4, C: 3, D: 2, F: 1 };

// MARK: Activity.swift

export const ACTIVITY_TYPES = [
  'visit',
  'call',
  'email',
  'text',
  'note',
  'meeting',
  'task',
  'stageChange',
  'assignment',
  'created',
  'photo',
  'document',
] as const;
export type ActivityType = (typeof ACTIVITY_TYPES)[number];
export const ACTIVITY_TYPE_TITLES: Readonly<Record<ActivityType, string>> = {
  visit: 'Visit',
  call: 'Call',
  email: 'Email',
  text: 'Text',
  note: 'Note',
  meeting: 'Meeting',
  task: 'Task',
  stageChange: 'Stage Changed',
  assignment: 'Assigned',
  created: 'Created',
  photo: 'Photo',
  document: 'Document',
};
/** `ActivityType.loggable`: types a rep can log by hand (the rest are written by the app itself). */
export const LOGGABLE_ACTIVITY_TYPES: readonly ActivityType[] = ['visit', 'call', 'email', 'text', 'note', 'meeting', 'task'];
/** `ActivityType.schedulable`: types that can be scheduled for the future. */
export const SCHEDULABLE_ACTIVITY_TYPES: readonly ActivityType[] = ['visit', 'call', 'email', 'text', 'meeting', 'task'];

export const ACTIVITY_STATUSES = ['completed', 'scheduled', 'cancelled'] as const;
export type ActivityStatus = (typeof ACTIVITY_STATUSES)[number];

/** `Attachment.Kind`. */
export const ATTACHMENT_KINDS = ['photo', 'document', 'businessCard', 'audio'] as const;
export type AttachmentKind = (typeof ATTACHMENT_KINDS)[number];

// MARK: User.swift

/** `UserRole` (same strings as the server's membership role, `Role` in wire.ts). */
export const USER_ROLES = ['admin', 'manager', 'rep'] as const;
export type UserRole = (typeof USER_ROLES)[number];
export const USER_ROLE_TITLES: Readonly<Record<UserRole, string>> = {
  admin: 'Admin',
  manager: 'Manager',
  rep: 'Sales Rep',
};

// MARK: Territory.swift

export const TERRITORY_KINDS = ['custom', 'postalCodes', 'county'] as const;
export type TerritoryKind = (typeof TERRITORY_KINDS)[number];
export const TERRITORY_KIND_TITLES: Readonly<Record<TerritoryKind, string>> = {
  custom: 'Custom map area',
  postalCodes: 'ZIP / postal codes',
  county: 'County',
};

// MARK: Route.swift

export const ROUTE_STATUSES = ['planned', 'active', 'completed'] as const;
export type RouteStatus = (typeof ROUTE_STATUSES)[number];

// MARK: Pipeline.swift

export const CUSTOM_FIELD_TYPES = [
  'text',
  'multilineText',
  'number',
  'currency',
  'date',
  'checkbox',
  'dropdown',
  'multiSelect',
  'phone',
  'email',
  'url',
] as const;
export type CustomFieldType = (typeof CUSTOM_FIELD_TYPES)[number];
export const CUSTOM_FIELD_TYPE_TITLES: Readonly<Record<CustomFieldType, string>> = {
  text: 'Text',
  multilineText: 'Long text',
  number: 'Number',
  currency: 'Currency',
  date: 'Date',
  checkbox: 'Checkbox',
  dropdown: 'Dropdown',
  multiSelect: 'Multi-select',
  phone: 'Phone',
  email: 'Email',
  url: 'URL',
};

// MARK: CompanySettings.swift

export const DISTANCE_UNITS = ['miles', 'kilometers'] as const;
export type DistanceUnit = (typeof DISTANCE_UNITS)[number];
export const DISTANCE_UNIT_TITLES: Readonly<Record<DistanceUnit, string>> = { miles: 'Miles', kilometers: 'Kilometers' };

/** `DistanceUnit.format(meters:)`: "%.1f mi" / "%.1f km". */
export function formatDistance(unit: DistanceUnit, meters: number): string {
  return unit === 'miles' ? `${(meters / 1609.344).toFixed(1)} mi` : `${(meters / 1000).toFixed(1)} km`;
}

/** `MapColorize`: how pins are colored on the map (`CompanySettings.defaultMapColorize`). */
export const MAP_COLORIZES = ['visitResult', 'stage', 'priority', 'owner', 'recordType', 'daysSinceVisit'] as const;
export type MapColorize = (typeof MAP_COLORIZES)[number];
export const MAP_COLORIZE_TITLES: Readonly<Record<MapColorize, string>> = {
  visitResult: 'Last Visit Result',
  stage: 'Stage',
  priority: 'Priority',
  owner: 'Owner',
  recordType: 'Record Type',
  daysSinceVisit: 'Days Since Visit',
};

export const INTEGRATION_KINDS = ['webhook', 'zapier', 'salesforce', 'hubspot', 'csv'] as const;
export type IntegrationKind = (typeof INTEGRATION_KINDS)[number];
export const INTEGRATION_KIND_TITLES: Readonly<Record<IntegrationKind, string>> = {
  webhook: 'Webhook',
  zapier: 'Zapier',
  salesforce: 'Salesforce',
  hubspot: 'HubSpot',
  csv: 'CSV Import / Export',
};

/**
 * Event names an integration pushes. `IntegrationConfig.events` is a free `[String]` in Swift (so the payload type is
 * `string[]` and other strings survive), but these are the only ones the app knows and the default list.
 */
export const INTEGRATION_EVENTS = ['record.created', 'record.updated', 'activity.logged', 'stage.changed'] as const;
export type IntegrationEvent = (typeof INTEGRATION_EVENTS)[number];

// MARK: Automation.swift

/**
 * `MessageChannel` (`MessageTemplate.channel`). The plan calls it TemplateChannel; Swift's name is kept and the alias
 * below points at it.
 */
export const MESSAGE_CHANNELS = ['email', 'text'] as const;
export type MessageChannel = (typeof MESSAGE_CHANNELS)[number];
export type TemplateChannel = MessageChannel;
export const TEMPLATE_CHANNELS = MESSAGE_CHANNELS;
export const MESSAGE_CHANNEL_TITLES: Readonly<Record<MessageChannel, string>> = { email: 'Email', text: 'Text' };

export const AUTOPLAY_STEP_TYPES = ['autoEmail', 'autoText', 'call', 'visit', 'task'] as const;
export type AutoplayStepType = (typeof AUTOPLAY_STEP_TYPES)[number];
export const AUTOPLAY_STEP_TYPE_TITLES: Readonly<Record<AutoplayStepType, string>> = {
  autoEmail: 'Auto Email',
  autoText: 'Auto Text',
  call: 'Call',
  visit: 'Visit',
  task: 'Task',
};
/** `AutoplayStepType.isAutomatic`: the step sends by itself instead of creating a task for the rep. */
export function isAutomaticStep(type: AutoplayStepType): boolean {
  return type === 'autoEmail' || type === 'autoText';
}

export const AUTOPLAY_TRIGGERS = ['manual', 'recordCreated', 'stageEntered', 'visitResult'] as const;
export type AutoplayTrigger = (typeof AUTOPLAY_TRIGGERS)[number];
export const AUTOPLAY_TRIGGER_TITLES: Readonly<Record<AutoplayTrigger, string>> = {
  manual: 'Manual enrollment',
  recordCreated: 'When a record is created',
  stageEntered: 'When a stage is entered',
  visitResult: 'When a visit result is logged',
};

export const ENROLLMENT_STATUSES = ['active', 'paused', 'completed', 'cancelled'] as const;
export type EnrollmentStatus = (typeof ENROLLMENT_STATUSES)[number];

// MARK: Engagement.swift

/**
 * `CommMessage.channel` is typed `ActivityType` in Swift (not a separate enum), so any ActivityType raw value decodes;
 * the app only writes these three (Swift comment `// .text, .email, .call`).
 */
export const COMM_CHANNELS: readonly ActivityType[] = ['text', 'email', 'call'];
export type CommChannel = ActivityType;

export const COMM_DIRECTIONS = ['outbound', 'inbound'] as const;
export type CommDirection = (typeof COMM_DIRECTIONS)[number];

export const GOAL_METRICS = [
  'visits',
  'contacts',
  'appointments',
  'deals',
  'revenue',
  'calls',
  'emails',
  'texts',
  'newRecords',
] as const;
export type GoalMetric = (typeof GOAL_METRICS)[number];
export const GOAL_METRIC_TITLES: Readonly<Record<GoalMetric, string>> = {
  visits: 'Visits',
  contacts: 'Contacts',
  appointments: 'Appointments',
  deals: 'Deals Won',
  revenue: 'Revenue',
  calls: 'Calls',
  emails: 'Emails',
  texts: 'Texts',
  newRecords: 'New Records',
};
/** `GoalMetric.isCurrency`: values are money in the company currency. */
export function isCurrencyMetric(metric: GoalMetric): boolean {
  return metric === 'revenue';
}

export const GOAL_PERIODS = ['daily', 'weekly', 'monthly', 'quarterly'] as const;
export type GoalPeriod = (typeof GOAL_PERIODS)[number];
export const GOAL_PERIOD_TITLES: Readonly<Record<GoalPeriod, string>> = {
  daily: 'Daily',
  weekly: 'Weekly',
  monthly: 'Monthly',
  quarterly: 'Quarterly',
};

/**
 * Every enum list keyed by its Swift type name, for the Swift cross-check and the golden `known-keys.json` check
 * (WEB_PLAN.md §2.4: "the TS enum lists equal the Swift allCases lists").
 */
export const SWIFT_ENUMS = {
  RecordType: RECORD_TYPES,
  Priority: PRIORITIES,
  ActivityType: ACTIVITY_TYPES,
  ActivityStatus: ACTIVITY_STATUSES,
  'Attachment.Kind': ATTACHMENT_KINDS,
  UserRole: USER_ROLES,
  TerritoryKind: TERRITORY_KINDS,
  RouteStatus: ROUTE_STATUSES,
  CustomFieldType: CUSTOM_FIELD_TYPES,
  DistanceUnit: DISTANCE_UNITS,
  MapColorize: MAP_COLORIZES,
  IntegrationKind: INTEGRATION_KINDS,
  MessageChannel: MESSAGE_CHANNELS,
  AutoplayStepType: AUTOPLAY_STEP_TYPES,
  AutoplayTrigger: AUTOPLAY_TRIGGERS,
  EnrollmentStatus: ENROLLMENT_STATUSES,
  CommDirection: COMM_DIRECTIONS,
  GoalMetric: GOAL_METRICS,
  GoalPeriod: GOAL_PERIODS,
} as const satisfies Record<string, readonly string[]>;

/** Titles keyed by Swift type name, for enums whose Swift type has a `title`. */
export const SWIFT_ENUM_TITLES = {
  RecordType: RECORD_TYPE_TITLES,
  Priority: PRIORITY_TITLES,
  ActivityType: ACTIVITY_TYPE_TITLES,
  UserRole: USER_ROLE_TITLES,
  TerritoryKind: TERRITORY_KIND_TITLES,
  CustomFieldType: CUSTOM_FIELD_TYPE_TITLES,
  DistanceUnit: DISTANCE_UNIT_TITLES,
  MapColorize: MAP_COLORIZE_TITLES,
  IntegrationKind: INTEGRATION_KIND_TITLES,
  MessageChannel: MESSAGE_CHANNEL_TITLES,
  AutoplayStepType: AUTOPLAY_STEP_TYPE_TITLES,
  AutoplayTrigger: AUTOPLAY_TRIGGER_TITLES,
  GoalMetric: GOAL_METRIC_TITLES,
  GoalPeriod: GOAL_PERIOD_TITLES,
} as const satisfies Record<string, Readonly<Record<string, string>>>;
